// Game Engine Service
// Handles all game-related API communication

import { httpClient } from './httpClient';
import { API_CONFIG } from './config';
import {
  GameType,
  CreateGameResponse,
  JoinGameResponse,
  GameStateResponse,
  DiceRollResponse,
  MoveResponse,
  PlayerGameResponse,
  ActiveGamesResponse,
} from './types';

/**
 * Game Engine API Service
 */
export class GameEngineService {
  private readonly endpoint: string;

  constructor() {
    this.endpoint = API_CONFIG.ENDPOINTS.GAME_ENGINE;
  }

  /**
   * Create a new game session
   */
  async createGame(
    gameType: GameType = 'classic-ludo',
    maxPlayers: number = 4,
    isPrivate: boolean = false
  ): Promise<CreateGameResponse> {
    return httpClient.post<CreateGameResponse>(`${this.endpoint}/create`, {
      gameType,
      maxPlayers,
      isPrivate,
    });
  }

  /**
   * Join an existing game
   */
  async joinGame(gameId: string): Promise<JoinGameResponse> {
    return httpClient.post<JoinGameResponse>(`${this.endpoint}/join`, {
      gameId,
    });
  }

  /**
   * Start a game (move from waiting to active phase)
   */
  async startGame(gameId: string): Promise<GameStateResponse> {
    return httpClient.post<GameStateResponse>(
      `${this.endpoint}/${gameId}/start`
    );
  }

  /**
   * Roll dice - server-generated cryptographically secure
   */
  async rollDice(gameId: string): Promise<DiceRollResponse> {
    return httpClient.post<DiceRollResponse>(
      `${this.endpoint}/${gameId}/roll-dice`
    );
  }

  /**
   * Make a move (legacy method name for compatibility)
   */
  async makeMove(
    gameId: string,
    pieceId: string,
    fromPosition: number,
    toPosition: number,
    diceValue: number
  ): Promise<MoveResponse> {
    return this.movePiece(gameId, pieceId, diceValue);
  }

  /**
   * Move a piece
   */
  async movePiece(
    gameId: string,
    pieceId: string,
    diceValue: number,
    fromPosition?: number,
    toPosition?: number
  ): Promise<MoveResponse> {
    // For now, we'll calculate positions on frontend
    // In a full implementation, these would come from the UI
    const moveData: any = {
      pieceId,
      diceValue,
    };

    // Add position data if provided
    if (fromPosition !== undefined && toPosition !== undefined) {
      moveData.fromPosition = fromPosition;
      moveData.toPosition = toPosition;
    } else {
      // Temporary: Use placeholder values for positions
      // The backend should ideally calculate these or we need to get them from the game state
      moveData.fromPosition = -1; // -1 typically represents home position
      moveData.toPosition = -1 + diceValue; // Simple calculation for demo
    }

    return httpClient.post<MoveResponse>(
      `${this.endpoint}/${gameId}/move`,
      moveData
    );
  }

  /**
   * Get current game state
   */
  async getGameState(gameId: string): Promise<GameStateResponse> {
    return httpClient.get<GameStateResponse>(
      `${this.endpoint}/${gameId}/state`
    );
  }

  /**
   * Leave a game
   */
  async leaveGame(gameId: string): Promise<GameStateResponse> {
    return httpClient.post<GameStateResponse>(
      `${this.endpoint}/${gameId}/leave`
    );
  }

  /**
   * Get player's current active game
   */
  async getPlayerGame(): Promise<PlayerGameResponse> {
    return httpClient.get<PlayerGameResponse>(`${this.endpoint}/player-game`);
  }

  /**
   * Get list of active games
   */
  async getActiveGames(): Promise<ActiveGamesResponse> {
    return httpClient.get<ActiveGamesResponse>(`${this.endpoint}/active`);
  }

  /**
   * Health check for game engine
   */
  async healthCheck(): Promise<{ success: boolean; status: string }> {
    return httpClient.get<{ success: boolean; status: string }>(
      `${this.endpoint}/health`
    );
  }
}

// Default game engine service instance
export const gameEngineService = new GameEngineService();
